<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import type { ApplicantEntry } from '@/types/Applicant';
import { ApplicantType } from '@/helpers/const';
import { getFullName } from '@/helpers/format';
import { TrashIcon } from '@heroicons/vue/24/outline';
import IndividualForm from './IndividualForm.vue';

export type PersonRole = 'director' | 'trustee';

const props = withDefaults(
  defineProps<{
    modelValue: ApplicantEntry;
    index: number;
    role: PersonRole;
    allIndividuals?: ApplicantEntry[];
    currentId?: string;
    forceShowErrors?: boolean;
    excludedApplicantIds?: string[];
  }>(),
  {
    allIndividuals: () => [],
    currentId: '',
    forceShowErrors: false,
    excludedApplicantIds: () => [],
  },
);

const emit = defineEmits<{
  (e: 'update:modelValue', v: ApplicantEntry): void;
  (e: 'remove', id: string): void;
  (e: 'relationship-change', individualId: string, newStatus: string, oldStatus?: string): void;
  (e: 'sync-applicant', applicant: ApplicantEntry): void;
}>();

const individualFormRef = ref<InstanceType<typeof IndividualForm>>();
const showEntry = ref(!props.modelValue.firstName || !props.modelValue.lastName);
const showModal = ref(false);

// Dynamic labels based on role
const roleLabel = computed(() => (props.role === 'director' ? 'Director' : 'Trustee'));
const roleNumber = computed(() => props.index + 1);

const person = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const displayName = computed(() => {
  const fullName = getFullName(props.modelValue);
  return fullName || `${roleLabel.value} ${roleNumber.value}`;
});

const editItem = () => {
  showEntry.value = true;
  individualFormRef.value?.resetForm({ values: props.modelValue });
};

const expandAndFocus = () => {
  editItem();
};

const saveChanges = async () => {
  const saved = (await individualFormRef.value?.update()) ?? false;
  if (saved) {
    showEntry.value = false;
  }
  return saved;
};

const cancelChanges = () => {
  individualFormRef.value?.cancel();
  if (!props.modelValue.firstName && !props.modelValue.lastName) {
    emit('remove', props.modelValue.id);
  } else {
    showEntry.value = false;
  }
};

const openModal = () => {
  showModal.value = true;
};

const removeItem = () => {
  showModal.value = false;
  emit('remove', props.modelValue.id);
};

const update = async () => {
  // If already saved and collapsed, return true without re-validating
  if (!showEntry.value && props.modelValue.firstName && props.modelValue.lastName) {
    return true;
  }
  return saveChanges();
};

defineExpose({ update, expandAndFocus });

watch(
  () => props.modelValue,
  (val) => {
    if (!val.firstName && !val.lastName) {
      showEntry.value = true;
    }
  },
  { immediate: true },
);
</script>

<template>
  <div class="person-card">
    <BxExpander
      v-model="showEntry"
      :header-background="!showEntry ? 'white' : 'none'"
      :header-bottom-radius="!showEntry"
      separator
      overflow-visible
    >
      <template #header>
        <div class="p-4">
          <template v-if="showEntry">
            <h4 class="flex items-center text-base font-medium leading-6 text-gray-900">
              <span class="mr-3 hidden md:block">
                <BxIcon name="customer" />
              </span>
              <span>{{ roleLabel }} {{ roleNumber }}</span>
            </h4>
          </template>
          <template v-else>
            <div class="-ml-4 -mt-3 flex flex-wrap items-center justify-between sm:flex-nowrap">
              <div class="ml-4 mt-3">
                <h4 class="flex items-center text-base font-medium leading-6 text-gray-900">
                  <span class="mr-3 hidden md:block">
                    <BxIcon name="customer" />
                  </span>
                  <span>{{ roleLabel }}: {{ displayName }}</span>
                </h4>
              </div>
              <div class="ml-4 mt-3 flex-shrink-0">
                <BxButton variant="secondary" size="sm" @click="editItem()">Edit</BxButton>
                <BxButton variant="link" size="sm" class="ml-2" @click="openModal()">Remove</BxButton>
              </div>
            </div>
          </template>
        </div>
      </template>
      <template #body>
        <div class="p-6">
          <IndividualForm
            ref="individualFormRef"
            v-model="person"
            :type="modelValue.type ?? ApplicantType.SECONDARY"
            :all-individuals="props.allIndividuals"
            :current-id="props.currentId"
            :force-show-errors="props.forceShowErrors"
            :excluded-applicant-ids="props.excludedApplicantIds"
            @relationship-change="(individualId: string, newStatus: string, oldStatus?: string) => emit('relationship-change', individualId, newStatus, oldStatus)"
            @sync-applicant="(applicant: ApplicantEntry) => emit('sync-applicant', applicant)"
          />
          <div class="mt-6 flex-shrink-0">
            <BxButton :id="`${props.role}-${props.index}-save`" variant="secondary" size="sm" @click="saveChanges()">
              Save
            </BxButton>
            <BxButton variant="link" size="sm" class="ml-4" @click="cancelChanges()">Cancel</BxButton>
          </div>
        </div>
      </template>
    </BxExpander>
    <BxModal
      :open="showModal"
      :icon="TrashIcon"
      :title="`Remove ${roleLabel.toLowerCase()}`"
      confirm-label="Remove"
      cancel-label="Cancel"
      @close="showModal = false"
      @confirm="removeItem()"
    >
      Do you want to remove
      <span class="text-bridgit-green">{{ displayName }}</span
      >?
    </BxModal>
  </div>
</template>

<style lang="scss" scoped>
.person-card {
  :deep(.bx-expander__header) {
    background-color: transparent;
  }

  :deep(.bx-expander__body) {
    background-color: transparent;
  }

  // Force collapse when not open - fixes nested expander CSS specificity issue
  :deep(.exp-panel:not(.exp-panel--open)) {
    .exp-panel__body {
      height: 0 !important;
      overflow: hidden;
    }
  }
}
</style>
